// Service to fetch users from the backend API
import { getIdToken } from '../contexts/AuthContext';
import { buildApiUrl, getAuthHeaders } from '../config/api';

const PROFILE_BATCH_SIZE = 200;

const fetchUserProfileBatch = async (unique, addError) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(buildApiUrl('/users/profiles'), {
      method: 'POST',
      headers: getAuthHeaders(idToken),
      body: JSON.stringify({ uids: unique })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      const errorMsg = errorData.message || errorData.error || `Failed to load user profiles: ${response.statusText}`;
      throw new Error(errorMsg);
    }

    const data = await response.json();
    return data.users || [];
  } catch (error) {
    const errorMsg = error.message || 'Error loading user profiles';
    if (addError) addError(errorMsg, error.stack, 'Users API');
    console.error('Error loading user profiles:', error);
    return [];
  }
};

/**
 * Looks up display profiles for a set of users by uid. Uids unknown to both
 * Firestore and Firebase Auth are left out of the result.
 * @param {Array<string>} uids - Firebase UIDs (sent in requests of up to 200)
 * @param {Function} addError - Optional error handler from ErrorContext
 * @returns {Promise<Array<{uid: string, name: string|null, email: string|null, profilePicturePath: string|null, initials: string|null}>>}
 */
export const fetchUserProfiles = async (uids, addError = null) => {
  const unique = [...new Set((uids || []).filter(Boolean))];
  if (unique.length === 0) return [];
  const chunks = [];
  for (let i = 0; i < unique.length; i += PROFILE_BATCH_SIZE) {
    chunks.push(unique.slice(i, i + PROFILE_BATCH_SIZE));
  }
  const results = await Promise.all(chunks.map(chunk => fetchUserProfileBatch(chunk, addError)));
  return results.flat();
};


/**
 * Looks up one user's display profile
 * @param {string} uid - Firebase UID
 * @param {Function} addError - Optional error handler from ErrorContext
 * @returns {Promise<{uid: string, name: string|null, email: string|null, profilePicturePath: string|null, initials: string|null}|null>}
 */
export const fetchUserProfile = async (uid, addError = null) => {
  const [profile] = await fetchUserProfiles([uid], addError);
  return profile || null;
};

/**
 * Fetches user details
 * @param {string} uid - User's Firebase UID
 * @param {Function} addError - Optional error handler from ErrorContext
 * @returns {Promise<import('../models/dataModels').UserDetails|null>} User details or null on error
 */
export const getUserDetails = async (uid, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(buildApiUrl(`/users/${uid}/details`), {
      headers: getAuthHeaders(idToken)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      const errorMsg = errorData.message || errorData.error || `Failed to fetch user details: ${response.statusText}`;
      throw new Error(errorMsg);
    }

    const userDetails = await response.json();
    return userDetails;
  } catch (error) {
    const errorMsg = error.message || `Error fetching details for user ${uid}`;
    if (addError) addError(errorMsg, error.stack, 'Users API');
    console.error(`Error fetching user details for ${uid}:`, error);
    return null;
  }
};

/**
 * Fetches recorded days for a user
 * @param {string} uid - User's Firebase UID
 * @param {Function} addError - Optional error handler from ErrorContext
 * @returns {Promise<Array<{day: string, fileCount: number}>>} Array of recorded days with file counts
 */
export const getRecordedDays = async (uid, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(buildApiUrl(`/users/${uid}/recorded-days`), {
      headers: getAuthHeaders(idToken)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      const errorMsg = errorData.message || errorData.error || `Failed to fetch recorded days: ${response.statusText}`;
      throw new Error(errorMsg);
    }

    const recordedDays = await response.json();
    return recordedDays;
  } catch (error) {
    const errorMsg = error.message || `Error fetching recorded days for user ${uid}`;
    if (addError) addError(errorMsg, error.stack, 'Users API');
    console.error(`Error fetching recorded days for ${uid}:`, error);
    return [];
  }
};

/**
 * Fetch one recorded day merged into a single track. Unlike getRecordedDayFiles
 * this throws on failure, so the map page can tell an error from an empty day.
 * @param {string} uid - User ID
 * @param {string} day - Day in MM_DD_YYYY format
 * @param {Function} addError - Optional error handler from ErrorContext
 * @returns {Promise<{ locations: Array<Object>, fileCount: number, failedFileCount: number }>}
 */
export const getRecordedDayLocations = async (uid, day, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(
      buildApiUrl(`/users/${uid}/recorded-days/${encodeURIComponent(day)}/locations`),
      {
        headers: getAuthHeaders(idToken)
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch recorded day: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    if (addError) addError(error.message || `Error fetching recorded day ${day}`, error.stack, 'Users API');
    console.error('Error fetching recorded day locations:', error);
    throw error;
  }
};

/**
 * Generate a merged dataset from selected files
 * @param {string} uid - User's Firebase UID
 * @param {string} day - Day folder name (MM_DD_YYYY format)
 * @param {Array<string>} filenames - Array of filenames to merge
 * @param {string} datasetName - Name for the new dataset
 * @param {Function} addError - Optional error handler from ErrorContext
 * @returns {Promise<Object>} Result object with success status and stats
 */
export const generateDataset = async (uid, day, filenames, datasetName, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(buildApiUrl(`/users/${uid}/generate-dataset`), {
      method: 'POST',
      headers: getAuthHeaders(idToken),
      body: JSON.stringify({ day, filenames, datasetName })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || data.error || `Failed to generate dataset: ${response.statusText}`);
    }

    return data;
  } catch (error) {
    const errorMsg = error.message || 'Error generating dataset';
    if (addError) addError(errorMsg, error.stack, 'Users API');
    console.error('Error generating dataset:', error);
    throw error;
  }
};

/**
 * Fetches files for a specific recorded day
 * @param {string} uid - User's Firebase UID
 * @param {string} day - Day folder name (MM_DD_YYYY format)
 * @param {Function} addError - Optional error handler from ErrorContext
 * @returns {Promise<Array<string>>} Array of filenames
 */
export const getRecordedDayFiles = async (uid, day, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(
      buildApiUrl(`/users/${uid}/recorded-days/${encodeURIComponent(day)}/files`),
      {
        headers: getAuthHeaders(idToken)
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      const errorMsg = errorData.message || errorData.error || `Failed to fetch recorded day files: ${response.statusText}`;
      throw new Error(errorMsg);
    }

    const files = await response.json();
    return files;
  } catch (error) {
    const errorMsg = error.message || `Error fetching files for day ${day}`;
    if (addError) addError(errorMsg, error.stack, 'Users API');
    console.error(`Error fetching recorded day files:`, error);
    return [];
  }
};

/**
 * Fetches the days that have log files for a user, with a file count per log type
 * @param {string} uid - User's Firebase UID
 * @param {Function} addError - Optional error handler from ErrorContext
 * @returns {Promise<Array<{day: string, appFileCount: number, gogglesFileCount: number}>>} Days, newest first
 */
export const getLogDays = async (uid, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(buildApiUrl(`/users/${uid}/logs`), {
      headers: getAuthHeaders(idToken)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      const errorMsg = errorData.message || errorData.error || `Failed to fetch logs: ${response.statusText}`;
      throw new Error(errorMsg);
    }

    return await response.json();
  } catch (error) {
    const errorMsg = error.message || `Error fetching logs for user ${uid}`;
    if (addError) addError(errorMsg, error.stack, 'Users API');
    console.error(`Error fetching logs for ${uid}:`, error);
    return [];
  }
};

/**
 * Fetches the log files of one type for a specific day
 * @param {string} uid - User's Firebase UID
 * @param {string} day - Day folder name (MM_DD_YYYY format)
 * @param {'app'|'goggles'} type - Which kind of log files to list
 * @param {Function} addError - Optional error handler from ErrorContext
 * @returns {Promise<Array<{name: string, size: number}>>} Array of files with size in bytes
 */
export const getLogDayFiles = async (uid, day, type, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(
      buildApiUrl(`/users/${uid}/logs/${encodeURIComponent(day)}/files?type=${encodeURIComponent(type)}`),
      {
        headers: getAuthHeaders(idToken)
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      const errorMsg = errorData.message || errorData.error || `Failed to fetch log files: ${response.statusText}`;
      throw new Error(errorMsg);
    }

    return await response.json();
  } catch (error) {
    const errorMsg = error.message || `Error fetching log files for day ${day}`;
    if (addError) addError(errorMsg, error.stack, 'Users API');
    console.error(`Error fetching log files:`, error);
    return [];
  }
};

/**
 * Fetches the raw text content of a specific log file (app or goggles)
 * @param {string} uid - User's Firebase UID
 * @param {string} day - Day folder name (MM_DD_YYYY format)
 * @param {string} filename - Log filename
 * @param {Function} addError - Optional error handler from ErrorContext
 * @returns {Promise<string|null>} Raw log text or null on error
 */
export const getLogContent = async (uid, day, filename, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(
      buildApiUrl(`/users/${uid}/log-content?day=${encodeURIComponent(day)}&filename=${encodeURIComponent(filename)}`),
      {
        headers: getAuthHeaders(idToken)
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      const errorMsg = errorData.message || errorData.error || `Failed to fetch log content: ${response.statusText}`;
      throw new Error(errorMsg);
    }

    const data = await response.json();
    return data.content;
  } catch (error) {
    const errorMsg = error.message || `Error fetching log content`;
    if (addError) addError(errorMsg, error.stack, 'Users API');
    console.error(`Error fetching log content:`, error);
    return null;
  }
};

// SessionDetailView downloads the app log matched to a LogsUploaded event under this name.
export const getAppLogContent = getLogContent;

/**
 * Resolves which app-log file (if any) corresponds to a LogsUploaded analytics event, so it
 * can be downloaded directly from the session timeline.
 * @param {string} uid - User's Firebase UID
 * @param {{eventTimestamp: string, tzOffsetSeconds?: number|null, pathToLog?: string|null}} eventInfo
 * @param {Function} addError - Optional error handler from ErrorContext
 * @returns {Promise<{matched: boolean, source?: string, day?: string, filename?: string, gapSeconds?: number, reason?: string, closestFilename?: string, closestGapSeconds?: number, tzOffsetUnknown?: boolean}|null>}
 *   null indicates the request itself failed (network/auth) - distinct from `matched: false`,
 *   which means the request succeeded but no log file could be confidently resolved.
 */
export const getLogForEvent = async (uid, { eventTimestamp, tzOffsetSeconds, pathToLog }, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const params = new URLSearchParams({ eventTimestamp });
    if (tzOffsetSeconds != null) params.set('tzOffsetSeconds', String(tzOffsetSeconds));
    if (pathToLog) params.set('pathToLog', pathToLog);

    const response = await fetch(
      buildApiUrl(`/users/${uid}/log-for-event?${params.toString()}`),
      {
        headers: getAuthHeaders(idToken)
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      const errorMsg = errorData.message || errorData.error || `Failed to resolve log for event: ${response.statusText}`;
      throw new Error(errorMsg);
    }

    return await response.json();
  } catch (error) {
    const errorMsg = error.message || `Error resolving log for event`;
    if (addError) addError(errorMsg, error.stack, 'Users API');
    console.error(`Error resolving log for event:`, error);
    return null;
  }
};

/**
 * Fetches crash details for a specific crash file
 * @param {string} uid - User's Firebase UID
 * @param {string} day - Day folder name (MM_DD_YYYY format)
 * @param {string} filename - Crash filename
 * @param {Function} addError - Optional error handler from ErrorContext
 * @returns {Promise<Object|null>} Decoded crash details or null on error
 */
export const getCrashDetails = async (uid, day, filename, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(
      buildApiUrl(`/users/${uid}/crash-details?day=${encodeURIComponent(day)}&filename=${encodeURIComponent(filename)}`),
      {
        headers: getAuthHeaders(idToken)
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      const errorMsg = errorData.message || errorData.error || `Failed to fetch crash details: ${response.statusText}`;
      throw new Error(errorMsg);
    }

    const crashDetails = await response.json();
    return crashDetails;
  } catch (error) {
    const errorMsg = error.message || `Error fetching crash details`;
    if (addError) addError(errorMsg, error.stack, 'Users API');
    console.error(`Error fetching crash details:`, error);
    return null;
  }
};

/**
 * Fetches goggles crash days for a user
 * @param {string} uid - User's Firebase UID
 * @param {Function} addError - Optional error handler from ErrorContext
 * @returns {Promise<Array<{day: string, fileCount: number}>>} Array of crash days with file counts
 */
export const getGogglesCrashes = async (uid, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(buildApiUrl(`/users/${uid}/goggles-crashes`), {
      headers: getAuthHeaders(idToken)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      const errorMsg = errorData.message || errorData.error || `Failed to fetch goggles crashes: ${response.statusText}`;
      throw new Error(errorMsg);
    }

    const crashDays = await response.json();
    return crashDays;
  } catch (error) {
    const errorMsg = error.message || `Error fetching goggles crashes for user ${uid}`;
    if (addError) addError(errorMsg, error.stack, 'Users API');
    console.error(`Error fetching goggles crashes for ${uid}:`, error);
    return [];
  }
};

/**
 * Desymbolicates a crash backtrace using addr2line
 * @param {string} uid - User's Firebase UID
 * @param {string} day - Day folder name (MM_DD_YYYY format)
 * @param {string} filename - Crash filename
 * @param {Function} addError - Optional error handler from ErrorContext
 * @returns {Promise<Object|null>} Desymbolicated crash data or null on error
 */
export const desymbolicate = async (uid, day, filename, addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(
      buildApiUrl(`/users/${uid}/desymbolicate?day=${encodeURIComponent(day)}&filename=${encodeURIComponent(filename)}`),
      {
        headers: getAuthHeaders(idToken)
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      const errorMsg = errorData.message || errorData.error || `Failed to desymbolicate: ${response.statusText}`;
      throw new Error(errorMsg);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    const errorMsg = error.message || `Error desymbolicating crash`;
    if (addError) addError(errorMsg, error.stack, 'Users API');
    console.error(`Error desymbolicating crash:`, error);
    throw error;
  }
};

/**
 * Search Firebase Auth users by exact uid, name or email
 * @param {string} query - Search query
 * @param {'name' | 'email' | 'both'} searchType - Type of search
 * @param {Function} addError - Optional error handler from ErrorContext
 * @returns {Promise<Array<{uid: string, email: string|null, displayName: string|null, firestoreName: string|null, profilePicturePath: string|null}>>}
 */
export const searchFirebaseUsers = async (query, searchType = 'both', addError = null) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      const errorMsg = 'Not authenticated. Please log in.';
      if (addError) addError(errorMsg, null, 'Authentication');
      throw new Error(errorMsg);
    }

    const params = new URLSearchParams({ query, searchType });
    const response = await fetch(buildApiUrl(`/users/search-firebase?${params}`), {
      headers: getAuthHeaders(idToken)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      const errorMsg = errorData.message || errorData.error || `Failed to search users: ${response.statusText}`;
      if (addError) addError(errorMsg, JSON.stringify(errorData, null, 2), 'API');
      throw new Error(errorMsg);
    }

    const data = await response.json();
    return data.users || [];
  } catch (error) {
    const errorMsg = error.message || 'Error searching Firebase users';
    if (addError) addError(errorMsg, error.stack, 'API');
    console.error('Error searching Firebase users:', error);
    throw error;
  }
};
